import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  CompareArrows as CompareArrowsIcon,
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon
} from '@mui/icons-material';
import { format } from 'date-fns';
import { useApi } from '../contexts/ApiContext';

const HgGhMatcher = () => {
  const { apiCall } = useApi();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [startDate, setStartDate] = useState('2025-08-23');
  const [endDate, setEndDate] = useState('2025-09-16');
  const [matchingResults, setMatchingResults] = useState(null);
  const [confirmedMatches, setConfirmedMatches] = useState(new Set());
  const [activeTab, setActiveTab] = useState(0);

  const handlePerformMatching = async () => {
    setLoading(true);
    setError(null);
    setConfirmedMatches(new Set());

    try {
      const response = await apiCall('/api/v1/hg-gh-matcher/match', 'POST', {
        startDate,
        endDate
      });
      setMatchingResults(response);
    } catch (err) {
      console.error('Error performing matching:', err);
      setError(err.message || 'Failed to perform matching');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmMatch = (potentialMatch, index) => {
    const matchKey = `${potentialMatch.pai.terminal}_${potentialMatch.pai.date}_${potentialMatch.pai.sequence}_${index}`;
    setConfirmedMatches(prev => new Set([...prev, matchKey]));

    if (matchingResults) {
      const updatedResults = { ...matchingResults };
      const confirmedMatch = {
        ...potentialMatch,
        matchType: 'MANUAL_CONFIRMED',
        matchConfidence: 100,
        confirmedAt: new Date().toISOString(),
        confirmedBy: 'User'
      };

      updatedResults.matches = [...updatedResults.matches, confirmedMatch];
      updatedResults.exactMatches = updatedResults.exactMatches + 1;
      updatedResults.potentialMatches = updatedResults.potentialMatches - 1;

      const newMatchRate = ((updatedResults.exactMatches / updatedResults.totalPaiTransactions) * 100).toFixed(2);
      updatedResults.summary.matchRate = newMatchRate;
      updatedResults.summary.exactMatches = updatedResults.exactMatches;
      updatedResults.summary.potentialMatches = updatedResults.potentialMatches;

      setMatchingResults(updatedResults);
    }
  };

  const isMatchConfirmed = (potentialMatch, index) => {
    const matchKey = `${potentialMatch.pai.terminal}_${potentialMatch.pai.date}_${potentialMatch.pai.sequence}_${index}`;
    return confirmedMatches.has(matchKey);
  };

  const handleExportCSV = async () => {
    try {
      const response = await apiCall('/api/v1/hg-gh-matcher/export', 'POST', { format: 'csv' });

      const blob = new Blob([response.content], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = response.filename || `hg_gh_matching_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError('Failed to export results');
    }
  };

  const formatDate = (date) => {
    if (!date) return '';
    if (date instanceof Date) {
      return format(date, 'MM/dd/yyyy');
    }
    return date;
  };

  const formatTime = (time) => {
    if (!time) return '';
    return time;
  };

  const getMatchIcon = (confidence) => {
    if (confidence >= 95) return <CheckCircleIcon color="success" fontSize="small" />;
    if (confidence >= 75) return <WarningIcon color="warning" fontSize="small" />;
    return <ErrorIcon color="error" fontSize="small" />;
  };

  const getMatchColor = (confidence) => {
    if (confidence >= 95) return 'success';
    if (confidence >= 75) return 'warning';
    return 'error';
  };

  return (
    <Box>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          GH Transaction Backup Matcher
        </Typography>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Compare GH terminal transactions between daily_transactions and daily_trans_gh_backup tables
        </Typography>

        <Grid container spacing={2} sx={{ mt: 2 }} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              color="primary"
              onClick={handlePerformMatching}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <CompareArrowsIcon />}
              fullWidth
            >
              {loading ? 'Matching...' : 'Perform Matching'}
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              onClick={handleExportCSV}
              disabled={!matchingResults || loading}
              startIcon={<DownloadIcon />}
              fullWidth
            >
              Export CSV
            </Button>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Paper>

      {matchingResults && (
        <>
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="subtitle2">
                    Current Table Count
                  </Typography>
                  <Typography variant="h5">
                    {matchingResults.summary.currentTableCount || matchingResults.summary.totalPaiTransactions}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="subtitle2">
                    Backup Table Count
                  </Typography>
                  <Typography variant="h5">
                    {matchingResults.summary.backupTableCount || matchingResults.summary.totalDailyTransactions}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="subtitle2">
                    Perfect Matches
                  </Typography>
                  <Typography variant="h5" color="success.main">
                    {matchingResults.summary.perfectMatches || matchingResults.summary.exactMatches}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom variant="subtitle2">
                    Match Rate
                  </Typography>
                  <Typography variant="h5" color={parseFloat(matchingResults.summary.matchRate) >= 75 ? 'success.main' : 'warning.main'}>
                    {matchingResults.summary.matchRate}%
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Matching Overview
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ width: '100%', mr: 1 }}>
                <LinearProgress
                  variant="determinate"
                  value={parseFloat(matchingResults.summary.matchRate)}
                  sx={{ height: 10, borderRadius: 5 }}
                />
              </Box>
              <Box sx={{ minWidth: 35 }}>
                <Typography variant="body2" color="text.secondary">
                  {matchingResults.summary.matchRate}%
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, mt: 1, flexWrap: 'wrap' }}>
              <Chip
                icon={<CheckCircleIcon />}
                label={`Perfect Matches: ${matchingResults.summary.perfectMatches || matchingResults.summary.exactMatches}`}
                color="success"
                size="small"
              />
              {confirmedMatches.size > 0 && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label={`Manual Confirmed: ${confirmedMatches.size}`}
                  color="success"
                  variant="outlined"
                  size="small"
                />
              )}
              <Chip
                icon={<WarningIcon />}
                label={`Field Mismatches: ${matchingResults.summary.fieldMismatches || matchingResults.summary.potentialMatches || 0}`}
                color="warning"
                size="small"
              />
              <Chip
                icon={<ErrorIcon />}
                label={`Only in Current: ${matchingResults.summary.onlyInCurrent || matchingResults.summary.unmatchedPai || 0}`}
                color="error"
                size="small"
              />
              <Chip
                icon={<InfoIcon />}
                label={`Only in Backup: ${matchingResults.summary.onlyInBackup || matchingResults.summary.unmatchedDaily || 0}`}
                color="default"
                size="small"
              />
            </Box>
          </Paper>

          {matchingResults.details && matchingResults.details.fieldMismatches && matchingResults.details.fieldMismatches.length > 0 && (
            <Accordion sx={{ mb: 3 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Field Mismatches ({matchingResults.summary.fieldMismatches} total)</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Terminal</TableCell>
                        <TableCell>Date/Time</TableCell>
                        <TableCell>Sequence</TableCell>
                        <TableCell>Differences</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {matchingResults.details.fieldMismatches.slice(0, 20).map((mismatch, index) => (
                        <TableRow key={index}>
                          <TableCell>{mismatch.terminal}</TableCell>
                          <TableCell>{formatDate(mismatch.date)} {formatTime(mismatch.time)}</TableCell>
                          <TableCell>{mismatch.sequence}</TableCell>
                          <TableCell>
                            {mismatch.differences.map((diff, idx) => (
                              <div key={idx}>
                                <strong>{diff.field}:</strong> {diff.current} → {diff.backup}
                              </div>
                            ))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </AccordionDetails>
            </Accordion>
          )}

          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Comparison Details
            </Typography>

            {matchingResults.details && matchingResults.details.onlyInCurrent && matchingResults.details.onlyInCurrent.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Only in Current Table ({matchingResults.summary.onlyInCurrent} total)
                </Typography>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Terminal</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell>Sequence</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Trace ID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {matchingResults.details.onlyInCurrent.slice(0, 50).map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.terminal}</TableCell>
                          <TableCell>{formatDate(row.date)}</TableCell>
                          <TableCell>{formatTime(row.time)}</TableCell>
                          <TableCell>{row.sequence}</TableCell>
                          <TableCell>${row.amount}</TableCell>
                          <TableCell>{row.trace_id}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {matchingResults.details && matchingResults.details.onlyInBackup && matchingResults.details.onlyInBackup.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Only in Backup Table ({matchingResults.summary.onlyInBackup} total)
                </Typography>
                <TableContainer sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Terminal</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Time</TableCell>
                        <TableCell>Sequence</TableCell>
                        <TableCell>Amount</TableCell>
                        <TableCell>Trace ID</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {matchingResults.details.onlyInBackup.slice(0, 50).map((row, index) => (
                        <TableRow key={index}>
                          <TableCell>{row.terminal}</TableCell>
                          <TableCell>{formatDate(row.date)}</TableCell>
                          <TableCell>{formatTime(row.time)}</TableCell>
                          <TableCell>{row.sequence}</TableCell>
                          <TableCell>${row.amount}</TableCell>
                          <TableCell>{row.trace_id}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Paper>
        </>
      )}
    </Box>
  );
};

export default HgGhMatcher;